/**
 * Responsive sidebar navigation.
 *
 * Desktop (>=1024px): standard sidebar, collapsible to icon-only via toggle.
 * Tablet (768-1023px): icon-only by default, expands on hover.
 * Mobile (<768px): hidden; shown as overlay drawer via hamburger menu.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { DASHBOARD_ROUTES } from '@worktracker/shared';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  AlertTriangle,
  Settings,
  ChevronLeft,
  ChevronRight,
  X,
  FileText,
} from 'lucide-react';

const COLLAPSE_KEY = 'sidebar-collapsed';

interface SidebarContextValue {
  readonly collapsed: boolean;
  readonly mobileOpen: boolean;
  readonly toggleCollapsed: () => void;
  readonly openMobile: () => void;
  readonly closeMobile: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }
  return ctx;
}

export function SidebarProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem(COLLAPSE_KEY) === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, String(collapsed));
  }, [collapsed]);

  const toggleCollapsed = useCallback(() => setCollapsed((c) => !c), []);
  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const value = useMemo(
    () => ({ collapsed, mobileOpen, toggleCollapsed, openMobile, closeMobile }),
    [collapsed, mobileOpen, toggleCollapsed, openMobile, closeMobile],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

interface NavItem {
  readonly label: string;
  readonly path: string | (() => string);
  readonly icon: typeof LayoutDashboard;
}

function getTodayPath(): string {
  const [datePart] = new Date().toLocaleDateString('sv-SE').split(' ');
  return `/daily/${datePart}`;
}

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  { label: 'Dashboard', path: DASHBOARD_ROUTES.DASHBOARD, icon: LayoutDashboard },
  { label: 'Daily View', path: getTodayPath, icon: CalendarDays },
  { label: 'Reports', path: DASHBOARD_ROUTES.REPORTS, icon: FileText },
  { label: 'Employees', path: DASHBOARD_ROUTES.EMPLOYEES, icon: Users },
  { label: 'Flags', path: DASHBOARD_ROUTES.FLAGS, icon: AlertTriangle },
  { label: 'Settings', path: DASHBOARD_ROUTES.SETTINGS, icon: Settings },
];

function navLinkClass(isActive: boolean): string {
  return cn(
    'flex items-center gap-3 rounded-lg px-3 min-h-[44px] text-sm font-medium transition-colors',
    'hover:bg-accent hover:text-accent-foreground',
    isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
  );
}

export function Sidebar() {
  const { collapsed, mobileOpen, toggleCollapsed, closeMobile } = useSidebar();
  const location = useLocation();
  const [hovered, setHovered] = useState(false);

  // Close mobile drawer on route change
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  // Close mobile drawer on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileOpen) {
        closeMobile();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileOpen, closeMobile]);

  const showLabels = hovered || !collapsed;

  const renderNavItems = (alwaysShowLabels: boolean) =>
    NAV_ITEMS.map((item) => {
      const path = typeof item.path === 'function' ? item.path() : item.path;
      return (
        <NavLink
          key={item.label}
          to={path}
          title={item.label}
          className={({ isActive }) => navLinkClass(isActive)}
        >
          <item.icon className="h-5 w-5 shrink-0" />
          {alwaysShowLabels ? (
            <span className="truncate">{item.label}</span>
          ) : (
            <span
              className={cn(
                'truncate whitespace-nowrap transition-opacity duration-150',
                showLabels || hovered ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden',
              )}
            >
              {item.label}
            </span>
          )}
        </NavLink>
      );
    });

  return (
    <>
      {/* Desktop / Tablet sidebar (hidden on mobile) */}
      <aside
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={cn(
          'hidden md:flex md:flex-col md:fixed md:inset-y-0 md:top-14 md:z-30',
          'border-r bg-card transition-[width] duration-200',
          'md:w-16',
          hovered && 'md:w-56',
          collapsed ? 'lg:w-16' : 'lg:w-56',
          hovered && 'lg:w-56',
        )}
      >
        <nav className="flex flex-1 flex-col gap-1 px-2 py-3 overflow-y-auto">
          {renderNavItems(false)}
        </nav>

        {/* Collapse toggle — desktop only */}
        <div className="hidden lg:flex border-t p-2">
          <button
            type="button"
            onClick={toggleCollapsed}
            className="flex w-full items-center justify-center rounded-lg min-h-[44px] text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
      </aside>

      {/* Mobile overlay drawer (<768px) */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div
            className="fixed inset-0 bg-foreground/20"
            onClick={closeMobile}
            aria-hidden="true"
          />
          <div className="relative z-50 flex w-64 flex-col bg-card shadow-lg">
            <div className="flex items-center justify-between border-b px-4 h-14">
              <span className="text-sm font-semibold">Menu</span>
              <button
                type="button"
                onClick={closeMobile}
                className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col gap-1 px-2 py-3 overflow-y-auto">
              {renderNavItems(true)}
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
